import dayjs from "dayjs";
import { useEffect } from "react";
import { FormControl } from "react-bootstrap";

export interface PayRollPickStartDateProps {
	// The current effective date (YYYY-MM-DD or any parseable date string); null shows the
	// nearest valid payroll boundary as a default.
	value: string | null;
	// Emits the selected date as YYYY-MM-DD (or null when cleared).
	onChange: (value: string | null) => void;
	readOnly?: boolean;

	// Field label (defaults to "Effective Date").
	label?: string;
	// Optional node rendered next to the label (e.g. a diff warning icon/popover).
	headerAccessory?: React.ReactNode;
}

// Payroll effective dates fall on 14-day boundaries starting 2005-12-24.
const getNearestValidDate = () => {
	const minDate = dayjs("2005-12-24");
	const nearestStepCount = Math.round(dayjs().diff(minDate, "day") / 14);
	return minDate.add(nearestStepCount * 14, "day").format("YYYY-MM-DD");
};

const PayRollPickStartDate: React.FC<PayRollPickStartDateProps> = ({
	value,
	onChange,
	readOnly = false,
	label = "Effective Date",
	headerAccessory,
}) => {
	const inputValue = (() => {
		if (!value) {
			return getNearestValidDate();
		}
		const parsed = dayjs(value);
		return parsed.isValid() ? parsed.format("YYYY-MM-DD") : getNearestValidDate();
	})();

	// When value is null/empty, the field displays getNearestValidDate() but the parent's
	// state is still null — if the user saves without touching the field, the parent would
	// send null instead of the displayed date. Push the displayed default back up on mount
	// so the parent's state always matches what's shown.
	// Mount-only: value/onChange changing later shouldn't re-fire this, only the initial null case needs seeding.
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once on mount only
	useEffect(() => {
		if (!value) {
			onChange(getNearestValidDate());
		}
	}, []);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value || null;
		onChange(raw ? dayjs(raw).format("YYYY-MM-DD") : null);
	};

	return (
		<div className="PayAdjusterFormItem">
			<div className="PayAdjusterFormItemHeader">
				<p>{label}</p>
				{headerAccessory}
			</div>
			<div className="PayAdjusterFormItemContent">
				<FormControl
					id="pay-adjuster-effective-date-date-picker"
					type="date"
					value={inputValue}
					onChange={handleChange}
					min="2005-12-24"
					step={14}
					readOnly={readOnly}
				/>
			</div>
		</div>
	);
};

export default PayRollPickStartDate;
